import { useState, useEffect, useCallback, useMemo } from 'react'
import { AlertTriangle, Filter, Clock, MapPin, Check, RefreshCcw } from 'lucide-react'
import { useAuthStore } from '../store/authStore'

// ─── i18n compact ─────────────────────────────────────────────────────────────
const _isEN = typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net'
const T = {
  title:       _isEN ? 'Incidents'                  : 'Incidentes',
  subtitle:    _isEN ? 'All incidents in the network' : 'Todos los incidentes de la red',
  refresh:     _isEN ? 'Refresh'                    : 'Refrescar',
  filters:     _isEN ? 'Filters'                    : 'Filtros',
  severity:    _isEN ? 'Severity'                   : 'Severidad',
  status:      _isEN ? 'Status'                     : 'Estado',
  site:        _isEN ? 'Site'                       : 'Sitio',
  all:         _isEN ? 'All'                        : 'Todas',
  empty:       _isEN ? 'No incidents match the filters' : 'Sin incidentes que coincidan',
  loading:     _isEN ? 'Loading…'                   : 'Cargando…',
  countLabel:  (n) => _isEN
    ? `${n} incident${n === 1 ? '' : 's'}`
    : `${n} incidente${n === 1 ? '' : 's'}`,
  openDetail:  _isEN ? 'Open detail'                : 'Ver detalle',
  ago:         (s) => _isEN ? `${s} ago`            : `hace ${s}`,
}

const SEV_LABEL = _isEN
  ? { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' }
  : { critical: 'Crítico', high: 'Alto', medium: 'Medio', low: 'Bajo' }
const STATUS_LABEL = _isEN
  ? { open: 'Open', acknowledged: 'Acknowledged', in_progress: 'In progress', resolved: 'Resolved', closed: 'Closed' }
  : { open: 'Abierto', acknowledged: 'Visto', in_progress: 'En curso', resolved: 'Resuelto', closed: 'Cerrado' }

const SEV_COLOR = {
  critical: { bg: 'rgba(190,24,93,0.15)',  fg: '#f472b6', dot: '#ec4899' },
  high:     { bg: 'rgba(220,38,38,0.15)',  fg: '#fca5a5', dot: '#ef4444' },
  medium:   { bg: 'rgba(217,119,6,0.15)',  fg: '#fcd34d', dot: '#f59e0b' },
  low:      { bg: 'rgba(5,150,105,0.15)',  fg: '#6ee7b7', dot: '#10b981' },
}
const STATUS_COLOR = {
  open:         { bg: 'rgba(239,68,68,0.12)',   fg: '#fca5a5' },
  acknowledged: { bg: 'rgba(245,158,11,0.12)',  fg: '#fcd34d' },
  in_progress:  { bg: 'rgba(59,130,246,0.12)',  fg: '#93c5fd' },
  resolved:     { bg: 'rgba(16,185,129,0.12)',  fg: '#6ee7b7' },
  closed:       { bg: 'rgba(107,114,128,0.15)', fg: '#d1d5db' },
}

// tiempo relativo tosco pero suficiente para demo
function relTime(iso) {
  if (!iso) return ''
  const now = Date.now()
  const then = new Date(iso).getTime()
  const s = Math.max(0, Math.round((now - then) / 1000))
  if (s < 60) return _isEN ? `${s}s ago` : `hace ${s}s`
  if (s < 3600) return _isEN ? `${Math.round(s/60)}m ago` : `hace ${Math.round(s/60)}m`
  if (s < 86400) return _isEN ? `${Math.round(s/3600)}h ago` : `hace ${Math.round(s/3600)}h`
  return _isEN ? `${Math.round(s/86400)}d ago` : `hace ${Math.round(s/86400)}d`
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function IncidentRow({ inc, onOpen }) {
  const sev = SEV_COLOR[inc.severity] || SEV_COLOR.medium
  const st  = STATUS_COLOR[inc.status] || STATUS_COLOR.open
  return (
    <div
      onClick={() => onOpen(inc)}
      style={{
        display: 'grid',
        gridTemplateColumns: '110px 110px 1fr 160px 90px',
        gap: 12, alignItems: 'center',
        padding: '10px 14px',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.03))'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{
        padding: '3px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600,
        background: sev.bg, color: sev.fg, textTransform: 'uppercase',
        letterSpacing: '0.05em', textAlign: 'center',
      }}>
        {SEV_LABEL[inc.severity] || inc.severity}
      </span>
      <span style={{
        padding: '3px 10px', borderRadius: 4, fontSize: 10, fontWeight: 500,
        background: st.bg, color: st.fg, textTransform: 'uppercase',
        letterSpacing: '0.05em', textAlign: 'center',
      }}>
        {STATUS_LABEL[inc.status] || inc.status}
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{
          fontSize: 13, color: 'var(--text-primary)', fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{inc.title}</span>
        {inc.description && (
          <span style={{
            fontSize: 11, color: 'var(--text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{inc.description}</span>
        )}
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
        <MapPin size={11} /> Site #{inc.site_id ?? '—'}
      </span>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
        <Clock size={11} /> {relTime(inc.created_at)}
      </span>
    </div>
  )
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function IncidentsView({ onOpenIncident }) {
  const token = useAuthStore(s => s.token)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [sevFilter, setSevFilter] = useState('all')
  const [statFilter, setStatFilter] = useState('all')
  const [siteFilter, setSiteFilter] = useState('all')
  const [sites, setSites] = useState([])

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      // limit alto para demo — trae hasta 200
      const r = await fetch('/api/incidents/?limit=200', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (r.ok) {
        const data = await r.json()
        setItems(data.items || [])
      }
    } finally { setLoading(false) }
  }, [token])

  const loadSites = useCallback(async () => {
    if (!token) return
    try {
      const r = await fetch('/api/sites/?limit=100', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (r.ok) setSites(await r.json())
    } catch {/* silent */}
  }, [token])

  useEffect(() => { load(); loadSites() }, [load, loadSites])
  useEffect(() => {
    const t = setInterval(load, 30000)
    return () => clearInterval(t)
  }, [load])

  const filtered = useMemo(() => {
    return items.filter(i =>
      (sevFilter === 'all' || i.severity === sevFilter) &&
      (statFilter === 'all' || i.status === statFilter) &&
      (siteFilter === 'all' || String(i.site_id) === String(siteFilter))
    )
  }, [items, sevFilter, statFilter, siteFilter])

  // Ordenar por created_at DESC (backend suele hacerlo pero por si acaso)
  const sorted = useMemo(() => [...filtered].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  ), [filtered])

  const openIncident = (inc) => {
    if (onOpenIncident) return onOpenIncident(inc)
    window.dispatchEvent(new CustomEvent('open-incident', { detail: { id: inc.id } }))
  }

  // Contadores por severidad (para chips arriba)
  const counts = useMemo(() => {
    const c = { critical: 0, high: 0, medium: 0, low: 0 }
    for (const i of items) c[i.severity] = (c[i.severity] || 0) + 1
    return c
  }, [items])

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-canvas)', overflow: 'hidden',
    }}>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '14px 20px',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <Chip color="critical" active={sevFilter === 'critical'}
                onClick={() => setSevFilter(sevFilter === 'critical' ? 'all' : 'critical')}
                label={`${counts.critical} ${SEV_LABEL.critical}`} />
          <Chip color="high" active={sevFilter === 'high'}
                onClick={() => setSevFilter(sevFilter === 'high' ? 'all' : 'high')}
                label={`${counts.high} ${SEV_LABEL.high}`} />
          <Chip color="medium" active={sevFilter === 'medium'}
                onClick={() => setSevFilter(sevFilter === 'medium' ? 'all' : 'medium')}
                label={`${counts.medium} ${SEV_LABEL.medium}`} />
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          <FilterSelect
            value={statFilter}
            onChange={setStatFilter}
            options={[
              { value: 'all', label: T.all },
              ...Object.entries(STATUS_LABEL).map(([v, l]) => ({ value: v, label: l })),
            ]}
            label={T.status}
          />
          {sites.length > 1 && (
            <FilterSelect
              value={siteFilter}
              onChange={setSiteFilter}
              options={[
                { value: 'all', label: T.all },
                ...sites.map(s => ({ value: String(s.id), label: s.name || `Site #${s.id}` })),
              ]}
              label={T.site}
            />
          )}
          <button
            onClick={load}
            style={{
              padding: '6px 12px', borderRadius: 4,
              border: '1px solid var(--border)', background: 'transparent',
              color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <RefreshCcw size={12} /> {T.refresh}
          </button>
        </div>
      </div>

      {/* Count */}
      <div style={{
        padding: '10px 20px',
        fontSize: 12, color: 'var(--text-muted)',
        borderBottom: '1px solid var(--border)',
      }}>
        {T.countLabel(sorted.length)}
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading && items.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            {T.loading}
          </div>
        )}
        {!loading && sorted.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            {T.empty}
          </div>
        )}
        {sorted.map(inc => (
          <IncidentRow key={inc.id} inc={inc} onOpen={openIncident} />
        ))}
      </div>
    </div>
  )
}

// ─── mini helpers ─────────────────────────────────────────────────────────────

function Chip({ color, label, active, onClick }) {
  const c = SEV_COLOR[color] || SEV_COLOR.medium
  return (
    <button
      onClick={onClick}
      style={{
        padding: '5px 12px', borderRadius: 4,
        background: active ? c.bg : 'transparent',
        border: `1px solid ${active ? c.fg : 'var(--border)'}`,
        color: active ? c.fg : 'var(--text-muted)',
        fontSize: 11, fontWeight: 500, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 4,
      }}
    >
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: c.dot, display: 'inline-block',
      }} />
      {label}
    </button>
  )
}

function FilterSelect({ value, onChange, options, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)' }}>
      {label}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          padding: '4px 8px', borderRadius: 4,
          border: '1px solid var(--border)',
          background: 'var(--bg-surface)', color: 'var(--text-primary)',
          fontSize: 11, cursor: 'pointer',
        }}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  )
}
